import { z } from 'zod/v4';

const HEIGHT_MIN = 0;
// The host UI (`indent-control.component.ts`) enforces no upper bound at all — this cap is purely
// an AI-facing sanity guardrail against an absurd value like 999999.
const HEIGHT_MAX = 2000;

const indentHeightSchema = z.number().int().min(HEIGHT_MIN).max(HEIGHT_MAX);

/**
 * THE indent widget data shape as AI tools accept it — single source of truth shared by every
 * consumer that exposes `create_indent_widget`/`update_indent_widget`, so their field surface can
 * never drift apart (same reasoning as `partial-button-item.schema.ts` and the text/image twins).
 * Lives here — not in a specific app — because both `apps/mcp-backend`'s mutations and
 * `apps/mcp-server`'s tools need the exact same shape, and neither may import the other (an app may
 * depend on a package, never on another app).
 *
 * All three fields are tool-facing conveniences, not a literal mirror of the persisted entity:
 * the real widget only carries `data.isSynchronizeStyle` in `data` — `height`/`mobileHeight`
 * actually live on the envelope's `styles.height`/`mobileStyles.height` (see entity-core's
 * `indentStyleBagSchema`). Exposing that internal envelope/data/styles split to the model would
 * be confusing plumbing with no benefit; each caller's own execute()/handler maps these flat
 * fields onto the real envelope shape.
 */
export const indentWidgetDataSchema = z.object({
    height: indentHeightSchema.optional().describe('Desktop/tablet height in pixels. Defaults to 40 on create.'),
    mobileHeight: indentHeightSchema
        .optional()
        .describe(
            'Mobile height in pixels. Only takes visible effect while isSynchronizeStyle is false — while ' +
                'synchronized, the editor keeps it identical to height regardless of this value. Defaults to ' +
                'height when omitted.'
        ),
    isSynchronizeStyle: z
        .boolean()
        .optional()
        .describe('Whether mobile height stays synchronized with the desktop height. Defaults to true on create.'),
});

export type PartialIndentWidgetData = z.infer<typeof indentWidgetDataSchema>;
